import type { BackendFunctionsType } from "../../../backend/apiFunctions/functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { decodeStream, getBackendUrl, parseIncompleteJson } from "./utils";

export default async function apiFetchStream<
  K extends keyof BackendFunctionsType,
  F extends keyof ApiFunction<K>
>(
  categoryId: K,
  functionName: F,
  params: Parameters<ApiFunction<K>[F]>[0],
  errorText?: string | null | undefined
): Promise<ReturnType<ApiFunction<K>[F]>> {
  let result = await fetch(
    `${getBackendUrl()}/nextApi/${categoryId}/${functionName as string}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(params || {}),
    }
  );

  if (!result.ok) throw new Error(`${result.statusText}`);
  return result.body as any;
}

type ApiFunction<K extends keyof BackendFunctionsType> = {
  [F in keyof BackendFunctionsType[K]]: BackendFunctionsType[K][F] extends (
    params: infer P
  ) => Promise<infer R>
    ? (params: P) => R
    : never;
};

export function useStreamQuery<T>({
  queryFn,
  queryKey,
  parseChunk,
  onStreamComplete,
  onStreamChunk,
}: {
  queryFn: () => Promise<ReadableStream<T> | null | undefined>;
  queryKey: any[];
  parseChunk?: (chunk: string) => string;
  onStreamChunk?: (chunk: string) => void;
  onStreamComplete?: (fullValue: string) => void;
}) {
  const [currentJson, setCurrentJson] = useState<T | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  const [lastChunk, setLastChunk] = useState<string | null>(null);
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [fullValue, setFullValue] = useState<string | null>(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
  });

  useEffect(() => {
    if (isDecoding == true) return;
    decodeStream({
      stream: data as any,
      parseChunk,
      onStreamComplete: (fullValue) => {
        setFullValue(fullValue);
        setCurrentValue(fullValue);
        const parsed = parseIncompleteJson<T>(fullValue);
        if (parsed) setCurrentJson(parsed);
        setIsComplete(true);
        setIsDecoding(false);
        onStreamComplete?.(fullValue);
      },
      onChunkReceived: (chunk, currentValue) => {
        setIsDecoding(true);
        setCurrentValue(currentValue);
        const parsed = parseIncompleteJson<T>(currentValue);
        if (parsed) setCurrentJson(parsed);
        setLastChunk(chunk);
        onStreamChunk?.(chunk);
      },
    });

    return () => {
      setIsDecoding(false);
      setIsComplete(false);
    };
  }, [data]);

  return {
    data,
    isLoading,
    isError,
    error,
    isDecoding,
    isComplete,
    lastChunk,
    currentValue,
    fullValue: JSON.parse(fullValue as string),
    currentJson,
  };
}
